import { ChangeEventHandler, useState, useCallback, useRef } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { FiCamera, FiXCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@components';
import { useAuth, useCreatePostMutation } from '@hooks';
import { selectUser } from '@hooks/useAuth/selectors';

import EmojiPickerPopover from '../EmojiPickerPopover';
import { PICTURE_INITIAL_STATE } from './constants';
import Editor, { PostFields } from './Editor';
import * as S from './styles';

const NewPost = () => {
  const formMethods = useForm<PostFields>();
  const currentUser = useAuth(selectUser);

  const [picture, setPicture] = useState(PICTURE_INITIAL_STATE);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const { mutate, isLoading: isMutating } = useCreatePostMutation();
  const { t } = useTranslation();

  const postLength = formMethods.watch('message', '')?.length || 0;

  const handleResetForm = () => {
    formMethods.reset();
    handleResetPicture();
  };

  const handleResetPicture = useCallback(
    () => setPicture(PICTURE_INITIAL_STATE),
    [],
  );

  const handlePicture: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const image = e.target.files?.[0];

      if (image) {
        const fileSize = image.size / 1024 / 1024;

        if (fileSize > 4) {
          const message = t('pages.newsfeed.sizeImage');
          toast.error(message);
        } else {
          setPicture({
            file: image,
            previewURL: URL.createObjectURL(image),
          });
        }
      }
    },
    [t],
  );

  const handleCreatePost = ({ message }: PostFields) => {
    mutate(
      {
        message,
        picture: picture.file,
      },
      {
        onSuccess: () => handleResetForm(),
      },
    );
  };

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      const textarea = editorRef.current;

      if (!textarea) return;

      const { selectionStart, selectionEnd } = textarea;
      const currentValue = formMethods.getValues('message') || '';
      const newValue =
        currentValue.slice(0, selectionStart) +
        emoji +
        currentValue.slice(selectionEnd);

      formMethods.setValue('message', newValue, { shouldValidate: true });

      requestAnimationFrame(() => {
        const pos = selectionStart + emoji.length;
        textarea.setSelectionRange(pos, pos);
        textarea.focus();
      });
    },
    [formMethods],
  );

  return (
    <S.Container onSubmit={formMethods.handleSubmit(handleCreatePost)}>
      <FormProvider {...formMethods}>
        <S.ComposerHeader>
          <Avatar src={currentUser.avatar} alt={currentUser.name} />
          <span>
            Olá, <strong>{currentUser.name?.split(' ')[0]}</strong>! Compartilhe
            aqui o seu post
          </span>
        </S.ComposerHeader>

        <Editor ref={editorRef} />

        {picture.previewURL && (
          <S.PicturePreview>
            <img src={picture.previewURL} alt="Preview" />
            <button type="button" onClick={handleResetPicture}>
              <FiXCircle size="1rem" />
            </button>
          </S.PicturePreview>
        )}

        <S.Footer>
          <S.FooterActions>
            <S.ActionButton>
              <label htmlFor="composer-picture">
                <FiCamera size="1.25rem" />
                <input
                  id="composer-picture"
                  type="file"
                  accept="image/*"
                  onChange={handlePicture}
                />
              </label>
            </S.ActionButton>
            <EmojiPickerPopover onEmojiSelect={handleEmojiSelect} />
          </S.FooterActions>

          <S.FooterRight>
            <S.CharCount>{postLength}/1000</S.CharCount>
            <S.SubmitButton type="submit" disabled={isMutating}>
              Publicar
            </S.SubmitButton>
          </S.FooterRight>
        </S.Footer>
      </FormProvider>
    </S.Container>
  );
};

export default NewPost;
